import { lazy, type ComponentType } from "react";
import {
  VALID_SERVICE_SLUGS,
  VALID_COUNTY_SLUGS,
  VALID_CITY_GROUP_SLUGS,
} from "@shared/routes";

type PageModule = { default: ComponentType<any> };
type Loader = () => Promise<PageModule>;

export interface PreloadableComponent {
  (props: any): JSX.Element;
  preload: () => Promise<PageModule>;
}

/**
 * Code-split page wrapper. Renders via React.lazy normally, but once
 * `preload()` has resolved it renders the loaded component synchronously —
 * so a preloaded route never suspends (no blank flash replacing the
 * pre-rendered HTML on first mount).
 */
function lazyRoute(load: Loader): PreloadableComponent {
  let Loaded: ComponentType<any> | null = null;
  const Lazy = lazy(load);
  const Component = ((props: any) =>
    Loaded ? <Loaded {...props} /> : <Lazy {...props} />) as PreloadableComponent;
  Component.preload = () =>
    load().then((mod) => {
      Loaded = mod.default;
      return mod;
    });
  return Component;
}

export const Home = lazyRoute(() => import("@/pages/home"));
export const ServicePage = lazyRoute(() => import("@/pages/service"));
export const ServiceAreas = lazyRoute(() => import("@/pages/service-areas"));
export const CountyPage = lazyRoute(() => import("@/pages/county"));
export const CityGroupPage = lazyRoute(() => import("@/pages/city-group"));
export const Gallery = lazyRoute(() => import("@/pages/gallery"));
export const Contact = lazyRoute(() => import("@/pages/contact"));
export const PrivacyPolicy = lazyRoute(() => import("@/pages/privacy-policy"));
export const NotFound = lazyRoute(() => import("@/pages/not-found"));

/** Map a pathname to the page component that will render it. */
export function componentForPath(pathname: string): PreloadableComponent {
  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  if (path === "/") return Home;
  if (path === "/service-areas") return ServiceAreas;
  if (path === "/gallery") return Gallery;
  if (path === "/contact") return Contact;
  if (path === "/privacy-policy") return PrivacyPolicy;
  const serviceMatch = path.match(/^\/services\/([^/]+)$/);
  if (serviceMatch)
    return VALID_SERVICE_SLUGS.has(serviceMatch[1]) ? ServicePage : NotFound;
  const countyMatch = path.match(/^\/service-areas\/([^/]+)$/);
  if (countyMatch)
    return VALID_COUNTY_SLUGS.has(countyMatch[1]) ? CountyPage : NotFound;
  const groupMatch = path.match(/^\/([^/]+)$/);
  if (groupMatch)
    return VALID_CITY_GROUP_SLUGS.has(groupMatch[1]) ? CityGroupPage : NotFound;
  return NotFound;
}

/** Preload the chunk for the given pathname (before first render). */
export function preloadRoute(pathname: string): Promise<unknown> {
  return componentForPath(pathname).preload();
}

/** Preload every page chunk (used by the build-time pre-renderer). */
export function preloadAllRoutes(): Promise<unknown> {
  return Promise.all(
    [
      Home,
      ServicePage,
      ServiceAreas,
      CountyPage,
      CityGroupPage,
      Gallery,
      Contact,
      PrivacyPolicy,
      NotFound,
    ].map((c) => c.preload()),
  );
}
